// src/components/ContactSection.js

import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FiMail,
  FiMessageSquare,
  FiSend,
  FiCheck,
  FiAlertCircle,
} from 'react-icons/fi';

// ────── Keyframes ──────
const gradientFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const SectionContainer = styled.section`
  padding: var(--spacing-xxl) 0;
  position: relative;
  overflow: hidden;
  /* Dark gradient matching other sections */
  background: linear-gradient(
    -45deg,
    #0B1D3A,
    #152E4F,
    #0B1D3A
  );
  background-size: 300% 300%;
  animation: ${gradientFlow} 20s ease infinite;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 80% 20%, rgba(71, 181, 255, 0.08) 0%, transparent 30%),
    radial-gradient(circle at 20% 80%, rgba(26, 54, 93, 0.1) 0%, transparent 30%);
  opacity: 1;
  z-index: 0;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: var(--spacing-lg);
  position: relative;
  color: var(--white);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, var(--accent), rgba(71, 181, 255, 0.3));
    border-radius: 2px;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  max-width: 700px;
  margin: 0 auto var(--spacing-xl);
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  line-height: 1.7;
`;

const ContactLayout = styled.div`
  display: flex;
  gap: var(--spacing-xl);
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const ContactInfo = styled(motion.div)`
  flex: 1;
  background-color: var(--primary);
  color: var(--white);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  display: flex;
  flex-direction: column;
  
  @media (max-width: 992px) {
    order: 2;
  }
`;

const ContactInfoTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-lg);
  color: var(--white);
`;

const ContactItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
`;

const ContactIconContainer = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-right: var(--spacing-md);
`;

const ContactItemContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContactItemTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--white);
`;

const ContactItemText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  line-height: 1.5;
  
  a {
    color: inherit;
    text-decoration: none;
    transition: color var(--transition-fast);
    
    &:hover {
      color: var(--accent);
    }
  }
`;

const ContactForm = styled(motion.div)`
  flex: 1.5;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 992px) {
    order: 1;
  }
`;

const ContactFormTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-lg);
  color: var(--white);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FormRow = styled.div`
  display: flex;
  gap: var(--spacing-md);
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const FormField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: var(--spacing-xs);
  color: rgba(255, 255, 255, 0.9);
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  transition: border-color var(--transition-fast);
  background: rgba(255, 255, 255, 0.08);
  color: var(--white);
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(71, 181, 255, 0.2);
  }
  
  ${props => props.error && `
    border-color: var(--error);
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.2);
    }
  `}
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  transition: border-color var(--transition-fast);
  background: rgba(255, 255, 255, 0.08);
  color: var(--white);
  
  option {
    background: #1A365D;
    color: var(--white);
  }
  
  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(71, 181, 255, 0.2);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  font-family: var(--font-main);
  transition: border-color var(--transition-fast);
  background: rgba(255, 255, 255, 0.08);
  color: var(--white);
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(71, 181, 255, 0.2);
  }
  
  ${props => props.error && `
    border-color: var(--error);
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.2);
    }
  `}
`;

const ErrorMessage = styled.div`
  color: var(--error);
  font-size: 0.8rem;
  margin-top: 4px;
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1rem 2rem;
  background: var(--accent);
  color: var(--white);
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-top: var(--spacing-md);
  
  svg {
    margin-left: var(--spacing-sm);
  }
  
  &:hover {
    background: #3AA8FF;
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.15);
    cursor: not-allowed;
    transform: none;
  }
`;

const FormMessage = styled.div`
  padding: var(--spacing-md);
  border-radius: var(--border-radius-md);
  margin-top: var(--spacing-md);
  display: flex;
  align-items: center;
  
  svg {
    margin-right: var(--spacing-sm);
    font-size: 1.25rem;
  }
  
  ${props => props.type === 'success' && `
    background-color: rgba(40, 167, 69, 0.1);
    color: var(--success);
    border: 1px solid rgba(40, 167, 69, 0.2);
  `}
  
  ${props => props.type === 'error' && `
    background-color: rgba(220, 53, 69, 0.1);
    color: var(--error);
    border: 1px solid rgba(220, 53, 69, 0.2);
  `}
`;

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6
    }
  }
};

const slideInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6
    }
  }
};

const ContactSection = () => {
  const [titleRef, titleInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [formRef, formInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [infoRef, infoInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    topic: 'General Inquiry',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Strict Name Validation: Prevent typing numbers/symbols
    if (name === 'name') {
      // Allow only letters and spaces (including accents)
      const sanitizedValue = value.replace(/[^a-zA-Z\u00C0-\u00FF\s]/g, '');

      setFormState(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
    } else {
      setFormState(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formState.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!/^[a-zA-Z\u00C0-\u00FF\s]*$/.test(formState.name)) {
      newErrors.name = 'Name should only contain letters and spaces';
    }

    if (!formState.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formState.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formState.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formState.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      // Simulate form submission
      setTimeout(() => {
        setIsSubmitting(false);
        setFormMessage({
          type: 'success',
          text: 'Thank you for your message! We will get back to you shortly.'
        });

        // Reset form
        setFormState({
          name: '',
          email: '',
          subject: '',
          topic: 'General Inquiry',
          message: ''
        });

        // Clear success message after 5 seconds
        setTimeout(() => {
          setFormMessage(null);
        }, 5000);
      }, 1500);
    }
  };

  return (
    <SectionContainer id="contact">
      <BackgroundPattern />
      <ContentWrapper>
        <div ref={titleRef}>
          <SectionTitle
            initial="hidden"
            animate={titleInView ? "visible" : "hidden"}
            variants={fadeIn}
          >
            Get In Touch
          </SectionTitle>
          <SectionSubtitle
            initial="hidden"
            animate={titleInView ? "visible" : "hidden"}
            variants={fadeIn}
          >
            Have questions about our upcoming apps or interested in collaborating? We'd love to hear from you.
          </SectionSubtitle>
        </div>

        <ContactLayout>
          <ContactInfo
            ref={infoRef}
            initial="hidden"
            animate={infoInView ? "visible" : "hidden"}
            variants={slideInLeft}
          >
            <ContactInfoTitle>Contact Information</ContactInfoTitle>
            <ContactItems>
              <ContactItem>
                <ContactIconContainer>
                  <FiMail />
                </ContactIconContainer>
                <ContactItemContent>
                  <ContactItemTitle>Email</ContactItemTitle>
                  <ContactItemText>
                    <a href="mailto:hello@xadevs.com">hello@xadevs.com</a>
                  </ContactItemText>
                </ContactItemContent>
              </ContactItem>

              <ContactItem>
                <ContactIconContainer>
                  <FiMessageSquare />
                </ContactIconContainer>
                <ContactItemContent>
                  <ContactItemTitle>Social Media</ContactItemTitle>
                  <ContactItemText>
                    <a href="https://linkedin.com/company/xadevs" target="_blank" rel="noopener noreferrer">LinkedIn</a> | <a href="https://x.com/xadevs" target="_blank" rel="noopener noreferrer">Twitter</a> | <a href="https://github.com/xadevs" target="_blank" rel="noopener noreferrer">GitHub</a>
                  </ContactItemText>
                </ContactItemContent>
              </ContactItem>
            </ContactItems>

            <div style={{ marginTop: 'auto', paddingTop: 'var(--spacing-lg)' }}>
              <ContactInfoTitle>We're Working On</ContactInfoTitle>
              <ContactItemText>
                <strong>VolpeTrack</strong> - An AI-powered financial assistant to help manage your finances with ease
              </ContactItemText>
              <ContactItemText style={{ marginTop: 'var(--spacing-sm)' }}>
                <strong>HoldThen</strong> - A logistics platform for package forwarding, consolidation, and shipment visibility
              </ContactItemText>
            </div>
          </ContactInfo>

          <ContactForm
            ref={formRef}
            initial="hidden"
            animate={formInView ? "visible" : "hidden"}
            variants={slideInRight}
          >
            <ContactFormTitle>Send Us a Message</ContactFormTitle>
            <Form onSubmit={handleSubmit}>
              <FormRow>
                <FormField>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    error={errors.name}
                  />
                  {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                </FormField>

                <FormField>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                    error={errors.email}
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </FormField>
              </FormRow>

              <FormRow>
                <FormField>
                  <Label htmlFor="topic">Inquiry Type</Label>
                  <Select
                    id="topic"
                    name="topic"
                    value={formState.topic}
                    onChange={handleChange}
                  >
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Product Information">Product Information</option>
                    <option value="Beta Testing">Beta Testing Interest</option>
                    <option value="Partnership">Partnership Opportunity</option>
                    <option value="Feedback">Feedback</option>
                  </Select>
                </FormField>

                <FormField>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formState.subject}
                    onChange={handleChange}
                    error={errors.subject}
                  />
                  {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
                </FormField>
              </FormRow>

              <FormField>
                <Label htmlFor="message">Your Message *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  error={errors.message}
                />
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
              </FormField>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
                {isSubmitting ? null : <FiSend />}
              </SubmitButton>

              {formMessage && (
                <FormMessage type={formMessage.type}>
                  {formMessage.type === 'success' ? <FiCheck /> : <FiAlertCircle />}
                  {formMessage.text}
                </FormMessage>
              )}
            </Form>
          </ContactForm>
        </ContactLayout>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default ContactSection;
